import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { friendlyMessage } from "@/lib/errors";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Loader2, MessageSquare, ArrowRight, ArrowLeft, ChevronDown, Check } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { authApi } from '@/lib/api/services/auth';
import { useAuth } from '@/components/AuthProvider';
import { COUNTRY_CODES } from '@/lib/constants/countryCodes';
import {
  MAX_PHONE_DIGITS,
  validatePhoneForCountry,
  phoneOverLengthError,
} from '@/lib/constants/phoneValidation';
import { cn } from '@/lib/utils';
import { PhoneNumberInput } from '@/components/PhoneNumberInput';
import { OtpInput } from '@/components/OtpInput';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { LandingTermsOfService } from '@/pages/LandingTermsOfService';

interface OnboardingData {
  fullName: string;
  phoneNumber: string; // E.164 (country_code + phone_display)
  country_code: string; // e.g. +91
  phone_display: string; // national digits only
}

const ONBOARDING_STEPS = [
  { id: 'user', title: 'Your Details', description: 'Name and phone number' },
  { id: 'otp', title: 'Verification', description: 'Verify your phone number' },
];

type OnboardingFlowVariant = 'standalone' | 'inline';

/** Imperative handle so a parent (Auth.tsx) can drive the OTP-step back arrow. */
export interface OnboardingFlowHandle {
  /** Return from the OTP step to the details step. No-op on the details step. */
  back: () => void;
}

interface OnboardingFlowProps {
  initialData?: {
    fullName?: string;
  };
  variant?: OnboardingFlowVariant;
  /** Reports whether the OTP (verification) step is showing (and the display
   *  phone once on OTP), so a parent can render its own back control + a
   *  "code sent to <phone>" subtitle above a shared header. */
  onStepChange?: (isOtpStep: boolean, phoneNumberDisplay?: string) => void;
}

export const OnboardingFlow = forwardRef<OnboardingFlowHandle, OnboardingFlowProps>(
  function OnboardingFlow({ initialData, variant = 'standalone', onStepChange }, ref) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signupWithPhone } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [countryCodeOpen, setCountryCodeOpen] = useState(false);
  // Pre-ticked ("auto ticked") — the user can un-tick to withhold consent.
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  // Field-level validation errors, rendered directly under each input.
  const [fullNameError, setFullNameError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Referral code from the invite link (?ref= / ?referral_code=). No manual
  // entry — the organization step was removed for phone-first onboarding.
  const referralCodeFromUrl = searchParams.get('ref') || searchParams.get('referral_code') || '';

  const [data, setData] = useState<OnboardingData>({
    fullName: initialData?.fullName ?? '',
    phoneNumber: '',
    country_code: '+91',
    phone_display: '',
  });

  const updateData = (updates: Partial<OnboardingData>) => {
    setData(prev => ({ ...prev, ...updates }));
  };

  // Countdown timer for OTP resend
  useEffect(() => {
    if (otpCountdown > 0) {
      const timer = setTimeout(() => setOtpCountdown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCountdown]);

  // Auto-verify once all 6 digits are entered (no explicit button) and, on
  // success, create the account — mirrors the phone-OTP login UX. Guarded so it
  // fires once per completed code and never while a verify/signup is in flight
  // (so it can't loop on error; re-fires only when the user edits the code).
  useEffect(() => {
    if (currentStep === 1 && otpSent && otpCode.length === 6 && !otpVerified && !verifyingOTP && !loading) {
      (async () => {
        const verified = await attemptOtpVerification();
        if (verified) await finalizeSignup();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpCode, otpSent, currentStep]);

  // Let a parent (Auth.tsx) render the back arrow above its shared header and
  // drive it: back() returns from the OTP step to the details step.
  useImperativeHandle(ref, () => ({
    back: () => {
      setOtpSent(false);
      setOtpVerified(false);
      setOtpCode('');
      setOtpCountdown(0);
      setError('');
      setCurrentStep(0);
    },
  }), []);

  // Report whether the OTP step is showing (+ the display phone) so the parent
  // can toggle its back arrow and show the "code sent to <phone>" subtitle.
  const phoneNumberDisplay = `${data.country_code} ${data.phone_display.replace(/\D/g, '')}`;
  useEffect(() => {
    onStepChange?.(currentStep === 1, phoneNumberDisplay);
  }, [currentStep, phoneNumberDisplay, onStepChange]);

  const sendOtp = async (): Promise<boolean> => {
    const phoneError = validatePhoneForCountry(data.country_code, data.phone_display);
    if (phoneError) {
      setError(phoneError);
      return false;
    }

    setSendingOTP(true);
    setError('');
    try {
      const result = await authApi.sendOTP(data.phoneNumber.trim(), data.fullName, undefined, 'signup');
      if (result.data) {
        setOtpSent(true);
        setOtpCountdown(60);
        toast({ variant: "success",
          title: 'OTP Sent!',
          description: 'Please check your WhatsApp for the verification code.',
        });
        return true;
      }
      setError(result.error || 'Failed to send OTP. Please try again.');
      return false;
    } catch (err: any) {
      setError(friendlyMessage(err, 'Failed to send OTP. Please try again.'));
      return false;
    } finally {
      setSendingOTP(false);
    }
  };

  const attemptOtpVerification = async (): Promise<boolean> => {
    if (!otpCode || otpCode.length !== 6) {
      setError('Please enter a valid 6-digit OTP code');
      return false;
    }

    setVerifyingOTP(true);
    setError('');
    try {
      const result = await authApi.verifyOTP(data.phoneNumber.trim(), otpCode.trim());
      if (result.data && result.data.verified) {
        setOtpVerified(true);
        toast({ variant: "success",
          title: 'Phone verified!',
          description: 'Your phone number has been verified successfully.',
        });
        return true;
      }
      setError(result.error || 'Invalid OTP code');
      return false;
    } catch (err: any) {
      setError(friendlyMessage(err, 'Failed to verify OTP'));
      return false;
    } finally {
      setVerifyingOTP(false);
    }
  };

  // Create the account once the phone is verified, then land on /home. The
  // signup endpoint returns a full session, so no separate login step.
  const finalizeSignup = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await signupWithPhone(data.fullName.trim(), data.phoneNumber.trim(), otpCode.trim(), referralCodeFromUrl || undefined);
      if (result.success) {
        toast({
          title: 'Welcome to WeNext!',
          description: "Let's personalise your experience with a few quick questions.",
        });
        localStorage.setItem('new_signup', 'true');
        // Durable, one-shot marker so the NEXA AI widget auto-opens once the
        // new user finishes onboarding. Cleared by ChatWidget after it opens.
        localStorage.setItem('nexa_widget_intro_pending', 'true');
        navigate('/home');
      } else {
        setError(result.error || 'Signup failed');
      }
    } catch (err: any) {
      setError(friendlyMessage(err, 'Setup failed'));
    } finally {
      setLoading(false);
    }
  };

  const nextStep = async () => {
    // Step 0 → send OTP → step 1. Validate each field and surface the message
    // directly under the field (not just the top-level alert).
    if (currentStep === 0) {
      let hasError = false;

      if (!data.fullName.trim()) {
        setFullNameError('Please enter your full name');
        hasError = true;
      }

      const phoneValidationError = !data.phone_display.trim()
        ? 'Please enter your phone number'
        : validatePhoneForCountry(data.country_code, data.phone_display);
      if (phoneValidationError) {
        setPhoneError(phoneValidationError);
        hasError = true;
      }

      if (!termsAccepted) {
        setError('Please accept the Terms of Service to continue');
        hasError = true;
      }

      if (hasError) return;

      const sent = await sendOtp();
      if (sent) setCurrentStep(1);
      return;
    }

    // Step 1 → verify OTP → create account
    if (currentStep === 1) {
      if (otpVerified) {
        await finalizeSignup();
        return;
      }
      const verified = await attemptOtpVerification();
      if (verified) {
        // brief pause so the success state is visible, then create the account
        setTimeout(() => { finalizeSignup(); }, 500);
      }
      return;
    }
  };

  const prevStep = () => {
    if (currentStep === 1) {
      setOtpSent(false);
      setOtpVerified(false);
      setOtpCode('');
      setOtpCountdown(0);
      setError('');
    }
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full name</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="Your full name"
                value={data.fullName}
                onChange={(e) => {
                  updateData({ fullName: e.target.value });
                  if (fullNameError) setFullNameError('');
                }}
                aria-invalid={!!fullNameError}
                required
                className={cn('h-12', fullNameError && 'border-red-400 focus-visible:ring-red-400')}
              />
              {fullNameError && <p className="text-xs text-red-600">{fullNameError}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Phone number</Label>
              <PhoneNumberInput
                id="phoneNumber"
                className="h-12"
                countryCode={data.country_code}
                phoneDisplay={data.phone_display}
                invalid={!!phoneError || !!phoneOverLengthError(data.country_code, data.phone_display)}
                onCountryChange={(code) => {
                  const digits = (data.phone_display || '').replace(/\D/g, '');
                  updateData({ country_code: code, phoneNumber: digits ? `${code}${digits}` : code });
                  if (phoneError) setPhoneError('');
                }}
                onPhoneChange={(digits) => {
                  updateData({ phone_display: digits, phoneNumber: `${data.country_code}${digits}` });
                  if (phoneError) setPhoneError('');
                }}
              />
              {(phoneError || phoneOverLengthError(data.country_code, data.phone_display)) && (
                <p className="text-xs text-red-600">
                  {phoneError || phoneOverLengthError(data.country_code, data.phone_display)}
                </p>
              )}
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            {sendingOTP ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
                <Alert>
                  <AlertDescription>Sending verification code. Please wait...</AlertDescription>
                </Alert>
              </div>
            ) : (
              <>
                <OtpInput
                  value={otpCode}
                  onChange={(v) => {
                    setOtpCode(v);
                    if (error) setError('');
                  }}
                  disabled={verifyingOTP || loading}
                  autoFocus
                />

                {(verifyingOTP || loading) && (
                  <p className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" /> Verifying…
                  </p>
                )}

                <div className="flex items-center justify-end pt-1">
                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={otpCountdown > 0 || sendingOTP}
                    className="text-sm font-semibold text-[var(--secondary-color)] hover:underline disabled:no-underline disabled:opacity-60"
                  >
                    {otpCountdown > 0 ? `Resend in ${otpCountdown}s` : 'Resend code'}
                  </button>
                </div>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: {
        const phoneValid = validatePhoneForCountry(data.country_code, data.phone_display) === '';
        return !!data.fullName.trim() && !!data.phoneNumber && phoneValid && termsAccepted;
      }
      case 1:
        return (otpVerified || otpCode.length === 6) && !verifyingOTP && !loading;
      default:
        return false;
    }
  };

  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;
  const isStandalone = variant === 'standalone';

  const flowContent = (
    <>
      {/* Error message */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Step content */}
      <div>
        {renderStep()}
      </div>

      {/* Step 0 has a single full-width "Next" button. The OTP step (step 1) has
          NO nav buttons — it auto-verifies on 6 digits and shows its own
          top-left Back arrow. */}
      {currentStep === 0 && (
        <div className="flex justify-end">
          <Button onClick={nextStep} size="lg" className="!h-12 w-full">
            {(sendingOTP || loading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Next
          </Button>
        </div>
      )}

      {/* Consent line — terms are accepted implicitly by continuing. */}
      {currentStep === 0 && (
        <p className="text-center text-xs text-gray-600">
          By clicking Next, you agree to our{' '}
          <button
            type="button"
            onClick={() => setShowTermsDialog(true)}
            className="text-[var(--secondary-color)] hover:underline"
          >
            Terms of Service
          </button>{' '}
          and{' '}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--secondary-color)] hover:underline"
          >
            Privacy Policy
          </a>.
        </p>
      )}

      {/* Terms Dialog */}
      <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <LandingTermsOfService hideHeader={true} />
        </DialogContent>
      </Dialog>
    </>
  );

  if (isStandalone) {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center p-6">
        <Card className="w-full max-w-2xl md:max-h-[600px] xxl:max-h-[fit-content] overflow-auto rounded-[12px] border-0 shadow-[0_0_10px_0_rgba(0,0,0,0.06)]">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Get Started with WeNext</CardTitle>
            <CardDescription>Sign up with your phone number</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {flowContent}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {flowContent}
    </div>
  );
});
